/**
 * ScanTypeReviewStep.jsx
 * Review step between upload and processing for the Smart Scan feature.
 * Shows AI-detected scan type per file; user can override before confirming.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import API from '../services/api';

function SpinnerIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ animation: 'spin 1s linear infinite' }}>
      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
    </svg>
  );
}

function NormalScanIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="9" y1="13" x2="15" y2="13"/>
      <line x1="9" y1="17" x2="12" y2="17"/>
    </svg>
  );
}

function TabularScanIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <line x1="3" y1="9" x2="21" y2="9"/>
      <line x1="3" y1="15" x2="21" y2="15"/>
      <line x1="9" y1="9" x2="9" y2="21"/>
    </svg>
  );
}

function ConfidencePill({ confidence }) {
  const pct = Math.round((confidence || 0) * 100);
  const color = pct >= 80 ? '#16A34A' : pct >= 60 ? '#D97706' : '#DC2626';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: '0.7rem', fontWeight: 700, color,
      background: color + '18',
      padding: '2px 8px', borderRadius: 20,
    }}>
      {pct}% confidence
    </span>
  );
}

/* ─── Normal Scan illustration ─────────────────────────────────────────── */
function NormalDrawingDiagram() {
  return (
    <svg viewBox="0 0 320 200" style={{ width: '100%', maxWidth: 320, display: 'block', borderRadius: 8, border: '1px solid #E5E7EB' }}>
      {/* Background */}
      <rect width="320" height="200" fill="#F8FAFC"/>
      {/* Title block */}
      <rect x="210" y="150" width="100" height="42" fill="#EFF6FF" stroke="#BFDBFE" strokeWidth="1"/>
      <text x="215" y="163" fontSize="6" fill="#1D4ED8" fontFamily="monospace">DWG NO: 741-0541-00</text>
      <text x="215" y="172" fontSize="6" fill="#1D4ED8" fontFamily="monospace">TITLE: BACK BODY</text>
      <text x="215" y="181" fontSize="6" fill="#1D4ED8" fontFamily="monospace">MATERIAL: TITANIUM</text>
      <text x="215" y="190" fontSize="6" fill="#1D4ED8" fontFamily="monospace">SCALE: 2:1  SHEET: 1 OF 1</text>
      {/* Part outline — isometric-style component */}
      <ellipse cx="100" cy="90" rx="55" ry="30" fill="none" stroke="#374151" strokeWidth="1.5"/>
      <ellipse cx="100" cy="90" rx="35" ry="18" fill="none" stroke="#374151" strokeWidth="1" strokeDasharray="3,2"/>
      <line x1="45" y1="90" x2="45" y2="115" stroke="#374151" strokeWidth="1.5"/>
      <line x1="155" y1="90" x2="155" y2="115" stroke="#374151" strokeWidth="1.5"/>
      <ellipse cx="100" cy="115" rx="55" ry="30" fill="none" stroke="#374151" strokeWidth="1.5"/>
      <ellipse cx="100" cy="115" rx="35" ry="18" fill="none" stroke="#374151" strokeWidth="1" strokeDasharray="3,2"/>
      {/* Dimension: diameter */}
      <line x1="45" y1="75" x2="155" y2="75" stroke="#DC2626" strokeWidth="1" markerEnd="url(#arrow)"/>
      <line x1="155" y1="75" x2="45" y2="75" stroke="#DC2626" strokeWidth="1"/>
      <line x1="45" y1="70" x2="45" y2="80" stroke="#DC2626" strokeWidth="1"/>
      <line x1="155" y1="70" x2="155" y2="80" stroke="#DC2626" strokeWidth="1"/>
      <rect x="70" y="66" width="62" height="12" fill="#FEF2F2" rx="2"/>
      <text x="101" y="75" textAnchor="middle" fontSize="8" fill="#DC2626" fontFamily="monospace" fontWeight="bold">Ø1.1239±0.0005</text>
      {/* GD&T FCF */}
      <rect x="60" y="130" width="80" height="13" fill="none" stroke="#1D4ED8" strokeWidth="1"/>
      <line x1="80" y1="130" x2="80" y2="143" stroke="#1D4ED8" strokeWidth="1"/>
      <line x1="110" y1="130" x2="110" y2="143" stroke="#1D4ED8" strokeWidth="1"/>
      <text x="70" y="140" textAnchor="middle" fontSize="9" fill="#1D4ED8" fontFamily="monospace">⊕</text>
      <text x="95" y="140" textAnchor="middle" fontSize="7" fill="#1D4ED8" fontFamily="monospace">Ø.010</text>
      <text x="120" y="140" textAnchor="middle" fontSize="7" fill="#1D4ED8" fontFamily="monospace">A|B|C</text>
      {/* Edge break note */}
      <text x="175" y="90" fontSize="7" fill="#374151" fontFamily="monospace">.003 MAX</text>
      <text x="175" y="99" fontSize="7" fill="#374151" fontFamily="monospace">EDGE BREAK</text>
      {/* Surface texture */}
      <text x="170" y="120" fontSize="7" fill="#374151" fontFamily="monospace">Ra µin: 32</text>
      {/* Section label */}
      <text x="20" y="165" fontSize="7" fill="#6B7280" fontFamily="monospace">SECTION F-F</text>
      <text x="20" y="173" fontSize="7" fill="#6B7280" fontFamily="monospace">SCALE 2:1</text>
      {/* Grid lines */}
      <line x1="0" y1="0" x2="320" y2="0" stroke="#D1D5DB" strokeWidth="0.5"/>
      <line x1="0" y1="199" x2="320" y2="199" stroke="#D1D5DB" strokeWidth="0.5"/>
      <line x1="0" y1="0" x2="0" y2="200" stroke="#D1D5DB" strokeWidth="0.5"/>
      <line x1="319" y1="0" x2="319" y2="200" stroke="#D1D5DB" strokeWidth="0.5"/>
      {/* Label */}
      <rect x="8" y="8" width="130" height="14" rx="3" fill="#DCFCE7"/>
      <text x="14" y="18" fontSize="8" fill="#166534" fontFamily="sans-serif" fontWeight="bold">✓ Normal Scan — Part Drawing</text>
    </svg>
  );
}

/* ─── Tabular Scan illustration ─────────────────────────────────────────── */
function TabularDrawingDiagram() {
  const rows = [
    ['C25870-01','9MM','0.354','8.99','0.404','10.26'],
    ['C25870-02','11MM','0.433','11.00','0.483','12.27'],
    ['C25870-03','13MM','0.512','13.00','0.562','14.27'],
    ['C25870-04','15MM','0.591','15.01','0.641','16.28'],
    ['C25870-05','17MM','0.669','16.99','0.719','18.26'],
  ];
  return (
    <svg viewBox="0 0 320 200" style={{ width: '100%', maxWidth: 320, display: 'block', borderRadius: 8, border: '1px solid #E5E7EB' }}>
      <rect width="320" height="200" fill="#F8FAFC"/>
      {/* Part outline with letter callouts */}
      <ellipse cx="245" cy="105" rx="60" ry="35" fill="none" stroke="#374151" strokeWidth="1.5"/>
      <ellipse cx="245" cy="105" rx="40" ry="22" fill="none" stroke="#374151" strokeWidth="1" strokeDasharray="3,2"/>
      {/* Letter callout A */}
      <line x1="185" y1="105" x2="158" y2="90" stroke="#DC2626" strokeWidth="1"/>
      <circle cx="152" cy="87" r="8" fill="#FEF2F2" stroke="#DC2626" strokeWidth="1.5"/>
      <text x="152" y="91" textAnchor="middle" fontSize="10" fill="#DC2626" fontFamily="monospace" fontWeight="bold">A</text>
      {/* Letter callout B */}
      <line x1="305" y1="105" x2="320" y2="88" stroke="#1D4ED8" strokeWidth="1"/>
      <circle cx="315" cy="83" r="8" fill="#EFF6FF" stroke="#1D4ED8" strokeWidth="1.5"/>
      <text x="315" y="87" textAnchor="middle" fontSize="10" fill="#1D4ED8" fontFamily="monospace" fontWeight="bold">B</text>
      {/* Table */}
      {/* Header row */}
      <rect x="2" y="2" width="148" height="14" fill="#1F2937"/>
      <text x="4" y="12" fontSize="6" fill="#fff" fontFamily="monospace" fontWeight="bold">COMPONENT</text>
      <text x="46" y="12" fontSize="6" fill="#fff" fontFamily="monospace" fontWeight="bold">SIZE</text>
      <text x="65" y="9" fontSize="6" fill="#93C5FD" fontFamily="monospace" fontWeight="bold">A</text>
      <text x="63" y="15" fontSize="5" fill="#9CA3AF" fontFamily="monospace">INCH  MM</text>
      <text x="105" y="9" fontSize="6" fill="#FCA5A5" fontFamily="monospace" fontWeight="bold">B</text>
      <text x="103" y="15" fontSize="5" fill="#9CA3AF" fontFamily="monospace">INCH  MM</text>
      {/* Table rows */}
      {rows.map((r, i) => (
        <g key={i}>
          <rect x="2" y={16 + i * 14} width="148" height="14" fill={i % 2 === 0 ? '#fff' : '#F9FAFB'} stroke="#E5E7EB" strokeWidth="0.5"/>
          <text x="4"   y={26 + i * 14} fontSize="6.5" fill="#111827" fontFamily="monospace">{r[0]}</text>
          <text x="48"  y={26 + i * 14} fontSize="6.5" fill="#374151" fontFamily="monospace">{r[1]}</text>
          <text x="65"  y={26 + i * 14} fontSize="6.5" fill="#1D4ED8" fontFamily="monospace">{r[2]}</text>
          <text x="85"  y={26 + i * 14} fontSize="6.5" fill="#1D4ED8" fontFamily="monospace">{r[3]}</text>
          <text x="103" y={26 + i * 14} fontSize="6.5" fill="#B91C1C" fontFamily="monospace">{r[4]}</text>
          <text x="123" y={26 + i * 14} fontSize="6.5" fill="#B91C1C" fontFamily="monospace">{r[5]}</text>
        </g>
      ))}
      <text x="4" y="96" fontSize="6" fill="#6B7280" fontFamily="monospace" fontStyle="italic">…and 3 more size variants</text>
      {/* Connecting lines from letter callouts to table header */}
      <line x1="152" y1="95" x2="75" y2="16" stroke="#DC2626" strokeWidth="0.7" strokeDasharray="3,2" opacity="0.5"/>
      <line x1="315" y1="91" x2="110" y2="16" stroke="#1D4ED8" strokeWidth="0.7" strokeDasharray="3,2" opacity="0.5"/>
      {/* Label */}
      <rect x="155" y="160" width="158" height="14" rx="3" fill="#FFF7ED"/>
      <text x="161" y="170" fontSize="8" fill="#C2410C" fontFamily="sans-serif" fontWeight="bold">⊞ Tabular Scan — Assembly Drawing</text>
      {/* Grid */}
      <line x1="0" y1="0" x2="320" y2="0" stroke="#D1D5DB" strokeWidth="0.5"/>
      <line x1="0" y1="199" x2="320" y2="199" stroke="#D1D5DB" strokeWidth="0.5"/>
      <line x1="0" y1="0" x2="0" y2="200" stroke="#D1D5DB" strokeWidth="0.5"/>
      <line x1="319" y1="0" x2="319" y2="200" stroke="#D1D5DB" strokeWidth="0.5"/>
    </svg>
  );
}

/* ─── Collapsible Scan Type Guide ────────────────────────────────────────── */
function ScanTypeGuide() {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('normal');

  const tabBtn = (id, label, color) => (
    <button
      onClick={() => setActiveTab(id)}
      style={{
        flex: 1, padding: '9px 12px', border: 'none', cursor: 'pointer',
        borderRadius: activeTab === id ? '8px 8px 0 0' : 8,
        background: activeTab === id ? (id === 'normal' ? '#EFF6FF' : '#FFF7ED') : '#F3F4F6',
        color: activeTab === id ? color : '#6B7280',
        fontWeight: activeTab === id ? 700 : 500,
        fontSize: '0.83rem',
        borderBottom: activeTab === id ? `2px solid ${color}` : '2px solid transparent',
        transition: 'all 0.15s',
      }}
    >
      {label}
    </button>
  );

  const bullet = (text, sub) => (
    <li style={{ marginBottom: 6, lineHeight: 1.5 }}>
      <span style={{ fontWeight: 600, color: '#1F2937' }}>{text}</span>
      {sub && <span style={{ color: '#6B7280', fontWeight: 400 }}> — {sub}</span>}
    </li>
  );

  return (
    <div style={{ marginBottom: 20, borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
      {/* Toggle header */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', background: open ? '#1F2937' : '#F9FAFB',
          border: 'none', cursor: 'pointer', transition: 'background 0.15s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={open ? '#60A5FA' : '#6B7280'}
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span style={{ fontWeight: 700, fontSize: '0.88rem', color: open ? '#F9FAFB' : '#374151' }}>
            Scan Type Guide — What's the difference?
          </span>
          <span style={{
            fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 20,
            background: open ? '#374151' : '#E5E7EB', color: open ? '#9CA3AF' : '#6B7280',
          }}>
            {open ? 'Click to close' : 'Click to learn more'}
          </span>
        </div>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={open ? '#9CA3AF' : '#6B7280'}
          strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div style={{ background: '#fff', padding: '0 0 16px' }}>
          {/* Tab switcher */}
          <div style={{ display: 'flex', gap: 4, padding: '12px 16px 0', borderBottom: '1px solid #E5E7EB' }}>
            {tabBtn('normal', '📄 Normal Scan (Part Drawing)', '#1D4ED8')}
            <div style={{ width: 8 }} />
            {tabBtn('tabular', '⊞ Tabular Scan (Assembly Drawing)', '#C2410C')}
          </div>

          {/* Normal Scan tab */}
          {activeTab === 'normal' && (
            <div style={{ padding: '16px', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              {/* Diagram */}
              <div style={{ flex: '0 0 auto', maxWidth: 320 }}>
                <NormalDrawingDiagram />
                <p style={{ margin: '8px 0 0', fontSize: '0.72rem', color: '#6B7280', textAlign: 'center' }}>
                  Example: Drawing 741-0541-00 — Back Body, SFF (Titanium)
                </p>
              </div>
              {/* Description */}
              <div style={{ flex: '1 1 220px' }}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: '#EFF6FF', border: '1px solid #BFDBFE',
                  borderRadius: 8, padding: '6px 12px', marginBottom: 12,
                }}>
                  <span style={{ fontSize: '1rem' }}>📄</span>
                  <span style={{ fontWeight: 700, color: '#1D4ED8', fontSize: '0.88rem' }}>
                    Part Drawing / Detail Drawing (GD&T)
                  </span>
                </div>
                <p style={{ margin: '0 0 10px', fontSize: '0.82rem', color: '#374151', lineHeight: 1.6 }}>
                  Defines how to manufacture and inspect a <strong>single component</strong>.
                  All dimensions are written directly on the geometry with their exact values.
                </p>
                <ul style={{ margin: 0, padding: '0 0 0 16px', fontSize: '0.82rem', color: '#374151' }}>
                  {bullet('Direct numeric dimensions', 'values like Ø1.1239±0.0005, .003 MAX EDGE BREAK written on the geometry itself')}
                  {bullet('GD&T Feature Control Frames', 'symbols like ⊕, //, ⊙ with tolerance zones and datum references (A|B|C)')}
                  {bullet('Single part number', 'one component described per drawing (e.g. 741-0541-00)')}
                  {bullet('Multiple views', 'SECTION F-F, DETAIL J, DETAIL K etc. all showing the same single part')}
                  {bullet('Notes block', 'material, finish, surface texture (Ra µin: 32), edge break specs')}
                  {bullet('Title block', 'drawing number, scale, sheet count, originator, approval')}
                </ul>
                <div style={{
                  marginTop: 12, padding: '10px 12px', borderRadius: 8,
                  background: '#F0FDF4', border: '1px solid #BBF7D0',
                  fontSize: '0.78rem', color: '#166534',
                }}>
                  <strong>✓ Use Normal Scan when:</strong> the drawing shows one part with dimensions
                  written directly on the views (no lookup tables, no letter-coded variants).
                </div>
              </div>
            </div>
          )}

          {/* Tabular Scan tab */}
          {activeTab === 'tabular' && (
            <div style={{ padding: '16px', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              {/* Diagram */}
              <div style={{ flex: '0 0 auto', maxWidth: 320 }}>
                <TabularDrawingDiagram />
                <p style={{ margin: '8px 0 0', fontSize: '0.72rem', color: '#6B7280', textAlign: 'center' }}>
                  Example: C25870 series — 8 size variants (9 MM → 23 MM), letters A & B reference table
                </p>
              </div>
              {/* Description */}
              <div style={{ flex: '1 1 220px' }}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: '#FFF7ED', border: '1px solid #FED7AA',
                  borderRadius: 8, padding: '6px 12px', marginBottom: 12,
                }}>
                  <span style={{ fontSize: '1rem' }}>⊞</span>
                  <span style={{ fontWeight: 700, color: '#C2410C', fontSize: '0.88rem' }}>
                    Assembly Drawing / Family Drawing
                  </span>
                </div>
                <p style={{ margin: '0 0 10px', fontSize: '0.82rem', color: '#374151', lineHeight: 1.6 }}>
                  Covers <strong>multiple variants or assembled components</strong> in one drawing.
                  Dimensions are coded as letters (A, B, C…) whose values live in a lookup table.
                </p>
                <ul style={{ margin: 0, padding: '0 0 0 16px', fontSize: '0.82rem', color: '#374151' }}>
                  {bullet('Dimension variant table', 'rows like C25870-01 / 9MM, C25870-02 / 11MM … each row is a different size with its own dimension values')}
                  {bullet('Letter-coded callouts', 'geometry shows A, B, C circles — NOT numeric values — pointing into the table')}
                  {bullet('Multiple part numbers', 'same base part with -01, -02, -03 suffixes covering a size family')}
                  {bullet('Assembly BOM table', 'ITEM NO | PART NUMBER | DESCRIPTION | QTY listing every component')}
                  {bullet('Multi-config table', 'column headers are product variants (e.g. Single Extruder vs Double Extruder) with dimension rows')}
                  {bullet('Generates separate plan per variant', 'each row in the table gets its own inspection plan')}
                </ul>
                <div style={{
                  marginTop: 12, padding: '10px 12px', borderRadius: 8,
                  background: '#FFF7ED', border: '1px solid #FED7AA',
                  fontSize: '0.78rem', color: '#92400E',
                }}>
                  <strong>✓ Use Tabular Scan when:</strong> the drawing has a table of variants/components
                  and the geometry uses letter callouts (A, B, C) instead of direct numbers.
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * @param {object} props
 * @param {Array<{fileId, fileName, ccSymbols, batchId}>} props.uploadedFiles
 * @param {string}   props.apiKey
 * @param {function} props.onConfirm  — called with [{fileId, fileName, ccSymbols, batchId, scanType}]
 * @param {function} props.onBack
 */
export default function ScanTypeReviewStep({ uploadedFiles, apiKey, onConfirm, onBack }) {
  const [detecting, setDetecting] = useState(true);
  const [detectionError, setDetectionError] = useState(null);
  // Map of fileId → { detectedType, confidence, reasoning }
  const [detections, setDetections] = useState({});
  // Map of fileId → overridden type ('normal' | 'tabular')
  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setDetecting(true);
      setDetectionError(null);
      try {
        const fileIds = uploadedFiles.map(f => f.fileId);
        const results = await API.detectScanTypes(fileIds, apiKey);
        if (cancelled) return;
        const map = {};
        for (const r of (results || [])) {
          map[r.file_id] = {
            detectedType:     r.detected_type    || 'normal',
            confidence:       r.confidence       || 0.5,
            reasoning:        r.reasoning        || '',
            tableDescription: r.table_description || '',
            error:            r.error            || null,
          };
        }
        setDetections(map);
      } catch (err) {
        if (!cancelled) {
          setDetectionError(err?.error || 'Detection failed — defaulting all to Normal scan.');
          // Default everything to normal on error
          const map = {};
          for (const f of uploadedFiles) {
            map[f.fileId] = { detectedType: 'normal', confidence: 0.5, reasoning: '', tableDescription: '', error: 'Detection failed' };
          }
          setDetections(map);
        }
      } finally {
        if (!cancelled) setDetecting(false);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track which file cards have the table-description dropdown open
  const [expandedCards, setExpandedCards] = useState({});
  const toggleExpand = useCallback((fileId) => {
    setExpandedCards(prev => ({ ...prev, [fileId]: !prev[fileId] }));
  }, []);

  const toggleType = useCallback((fileId) => {
    setOverrides(prev => {
      const current = prev[fileId] || detections[fileId]?.detectedType || 'normal';
      return { ...prev, [fileId]: current === 'normal' ? 'tabular' : 'normal' };
    });
  }, [detections]);

  const getEffectiveType = (fileId) => {
    if (overrides[fileId]) return overrides[fileId];
    return detections[fileId]?.detectedType || 'normal';
  };

  const handleConfirm = () => {
    const filesWithTypes = uploadedFiles.map(f => ({
      ...f,
      scanType: getEffectiveType(f.fileId),
    }));
    onConfirm(filesWithTypes);
  };

  const normalCount  = uploadedFiles.filter(f => getEffectiveType(f.fileId) === 'normal').length;
  const tabularCount = uploadedFiles.filter(f => getEffectiveType(f.fileId) === 'tabular').length;

  return (
    <div style={{ maxWidth: 1140, margin: '0 auto', padding: '36px 40px 56px' }}>
      {/* Workflow stepper */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 26, userSelect: 'none' }}>
        {[
          { n: 1, label: 'Upload Drawings', state: 'done', onClick: onBack },
          { n: 2, label: 'Review Scan Types', state: 'active' },
          { n: 3, label: 'Analysis & Plan', state: 'todo' },
        ].map((s, i, arr) => (
          <React.Fragment key={s.n}>
            <div
              onClick={s.onClick}
              style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: s.onClick ? 'pointer' : 'default' }}
            >
              <div style={{
                width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.8rem', fontWeight: 800,
                background: s.state === 'active' ? 'var(--accent, #F97316)' : s.state === 'done' ? '#DCFCE7' : '#F1F5F9',
                color: s.state === 'active' ? '#fff' : s.state === 'done' ? '#15803D' : '#94A3B8',
                border: s.state === 'active' ? 'none' : '1.5px solid ' + (s.state === 'done' ? '#86EFAC' : '#E2E8F0'),
              }}>
                {s.state === 'done' ? '✓' : s.n}
              </div>
              <span style={{
                fontSize: '0.85rem', fontWeight: s.state === 'active' ? 800 : 600,
                color: s.state === 'active' ? 'var(--text-primary, #1A1410)' : s.state === 'done' ? '#15803D' : '#94A3B8',
                whiteSpace: 'nowrap',
              }}>
                {s.label}
              </span>
            </div>
            {i < arr.length - 1 && (
              <div style={{
                flex: 1, height: 2, margin: '0 14px', borderRadius: 2,
                background: s.state === 'done' ? '#86EFAC' : '#E2E8F0',
              }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Title + live stat tiles */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: '0 0 6px', fontSize: '1.6rem', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-primary, #1A1410)' }}>
          Review Scan Types
        </h2>
        <p style={{ margin: '0 0 18px', color: 'var(--text-secondary, #6B7280)', fontSize: '0.92rem' }}>
          AI has detected the best scan type for each drawing. Review and adjust if needed, then start the analysis.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
          {(() => {
            const confs = uploadedFiles
              .map(f => detections[f.fileId]?.confidence)
              .filter(c => typeof c === 'number');
            const avg = confs.length ? Math.round(confs.reduce((a, b) => a + b, 0) / confs.length * 100) : null;
            const tiles = [
              { v: uploadedFiles.length, l: 'Drawings', c: '#1A1410', bg: '#fff' },
              { v: normalCount, l: 'Normal Scans', c: '#2563EB', bg: '#EFF6FF' },
              { v: tabularCount, l: 'Tabular Scans', c: '#F97316', bg: '#FFF7ED' },
              { v: detecting ? '…' : (avg != null ? avg + '%' : '—'), l: 'Avg Detection Confidence', c: '#15803D', bg: '#F0FDF4' },
            ];
            return tiles.map(t => (
              <div key={t.l} style={{
                background: t.bg, border: '1px solid var(--border, #E5E7EB)', borderRadius: 12,
                padding: '14px 18px',
              }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: t.c, lineHeight: 1.1 }}>{t.v}</div>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6B7280', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.l}</div>
              </div>
            ));
          })()}
        </div>
      </div>

      {/* Scan Type Guide — collapsible explainer */}
      <ScanTypeGuide />

      {/* Detection loading banner */}
      {detecting && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 18px', borderRadius: 10,
          background: '#EFF6FF', border: '1px solid #BFDBFE',
          marginBottom: 20, color: '#1D4ED8', fontSize: '0.875rem',
        }}>
          <SpinnerIcon />
          <span>Analysing drawings to detect scan type…</span>
        </div>
      )}

      {/* Detection error banner */}
      {detectionError && !detecting && (
        <div style={{
          padding: '12px 16px', borderRadius: 10,
          background: '#FFF7ED', border: '1px solid #FDE8C8',
          marginBottom: 20, color: '#B45309', fontSize: '0.85rem',
        }}>
          ⚠ {detectionError}
        </div>
      )}

      {/* File cards — responsive grid fills the page width */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(480px, 1fr))',
        gap: 16, marginBottom: 28, alignItems: 'start',
      }}>
        {uploadedFiles.map(f => {
          const det = detections[f.fileId];
          const effective = getEffectiveType(f.fileId);
          const isOverridden = !!overrides[f.fileId];
          const isTabular = effective === 'tabular';

          return (
            <div key={f.fileId} style={{
              background: 'var(--surface, #fff)',
              border: '1px solid var(--border, #E5E7EB)',
              borderLeft: `5px solid ${isTabular ? '#F97316' : '#2563EB'}`,
              borderRadius: 14, padding: '18px 20px',
              display: 'flex', flexDirection: 'column', gap: 10,
              transition: 'border-color 0.15s, box-shadow 0.15s',
              boxShadow: '0 1px 3px rgba(16, 24, 40, 0.06), 0 1px 2px rgba(16, 24, 40, 0.04)',
            }}>
              {/* File name row */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 8,
                    background: isTabular ? '#FFF7ED' : '#F8FAFC',
                    border: `1.5px solid ${isTabular ? '#FDE8C8' : '#E5E7EB'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    color: isTabular ? '#F97316' : '#6B7280',
                  }}>
                    {isTabular ? <TabularScanIcon /> : <NormalScanIcon />}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      fontSize: '0.9rem', fontWeight: 600,
                      color: 'var(--text-primary, #1A1410)',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {f.fileName}
                    </div>
                    {det && !detecting && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
                        <ConfidencePill confidence={det.confidence} />
                        {isOverridden && (
                          <span style={{ fontSize: '0.7rem', color: '#D97706', fontWeight: 600 }}>
                            ✏ Overridden
                          </span>
                        )}
                      </div>
                    )}
                    {detecting && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                        <SpinnerIcon />
                        <span style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>Detecting…</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Type selector — clear segmented control */}
                <div style={{
                  display: 'inline-flex', flexShrink: 0, borderRadius: 10, overflow: 'hidden',
                  border: '1.5px solid var(--border, #E5E7EB)', opacity: detecting ? 0.5 : 1,
                }}>
                  <button
                    onClick={() => isTabular && toggleType(f.fileId)}
                    disabled={detecting}
                    title="Single-part drawing — dimensions written directly on the geometry"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '8px 14px', border: 'none', fontSize: '0.8rem', fontWeight: 700,
                      cursor: detecting ? 'not-allowed' : 'pointer',
                      background: !isTabular ? '#2563EB' : '#fff',
                      color: !isTabular ? '#fff' : '#6B7280',
                      transition: 'background 0.15s, color 0.15s',
                    }}
                  >
                    <NormalScanIcon /> Normal
                  </button>
                  <button
                    onClick={() => !isTabular && toggleType(f.fileId)}
                    disabled={detecting}
                    title="Family / assembly drawing — dimensions defined through a variant table"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '8px 14px', border: 'none', fontSize: '0.8rem', fontWeight: 700,
                      cursor: detecting ? 'not-allowed' : 'pointer',
                      background: isTabular ? '#F97316' : '#fff',
                      color: isTabular ? '#fff' : '#6B7280',
                      transition: 'background 0.15s, color 0.15s',
                    }}
                  >
                    <TabularScanIcon /> Tabular
                  </button>
                </div>
              </div>

              {/* Reasoning */}
              {det?.reasoning && !detecting && (
                <div style={{
                  fontSize: '0.78rem', color: '#6B7280',
                  padding: '8px 12px', background: '#F9FAFB',
                  borderRadius: 7, lineHeight: 1.4,
                }}>
                  {det.reasoning}
                </div>
              )}

              {/* Table description dropdown — only for tabular detections */}
              {det?.tableDescription && !detecting && isTabular && (
                <div style={{ borderTop: '1px solid #FDE8C8', paddingTop: 8 }}>
                  <button
                    onClick={() => toggleExpand(f.fileId)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                      display: 'flex', alignItems: 'center', gap: 6,
                      fontSize: '0.78rem', fontWeight: 600, color: '#F97316',
                      width: '100%', textAlign: 'left',
                    }}
                  >
                    <svg
                      width="13" height="13" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.5"
                      strokeLinecap="round" strokeLinejoin="round"
                      style={{
                        transition: 'transform 0.2s',
                        transform: expandedCards[f.fileId] ? 'rotate(90deg)' : 'rotate(0deg)',
                        flexShrink: 0,
                      }}
                    >
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                      <line x1="3" y1="9" x2="21" y2="9"/>
                      <line x1="3" y1="15" x2="21" y2="15"/>
                      <line x1="9" y1="9" x2="9" y2="21"/>
                    </svg>
                    Detected Table Details
                    <span style={{
                      marginLeft: 'auto', fontSize: '0.7rem', color: '#9CA3AF', fontWeight: 400,
                    }}>
                      {expandedCards[f.fileId] ? 'hide' : 'show'}
                    </span>
                  </button>

                  {expandedCards[f.fileId] && (
                    <div style={{
                      marginTop: 8, padding: '10px 14px',
                      background: '#FFF7ED', border: '1px solid #FDE8C8',
                      borderRadius: 8, fontSize: '0.8rem', color: '#92400E',
                      lineHeight: 1.6, whiteSpace: 'pre-wrap',
                    }}>
                      {det.tableDescription}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary + CTA */}
      <div style={{
        background: 'var(--surface, #fff)',
        border: '1.5px solid var(--border, #E5E7EB)',
        borderRadius: 14, padding: '18px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
        flexWrap: 'wrap',
        boxShadow: '0 4px 12px rgba(16, 24, 40, 0.08)',
        position: 'sticky', bottom: 16,
      }}>
        <div style={{ display: 'flex', gap: 20 }}>
          {normalCount > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: '#374151' }}>
              <NormalScanIcon />
              <span><strong>{normalCount}</strong> Normal Scan{normalCount !== 1 ? 's' : ''}</span>
            </div>
          )}
          {tabularCount > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: '#F97316' }}>
              <TabularScanIcon />
              <span><strong>{tabularCount}</strong> Tabular Scan{tabularCount !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
        <button
          onClick={handleConfirm}
          disabled={detecting}
          style={{
            background: detecting ? '#9CA3AF' : 'var(--accent, #F97316)',
            color: '#fff', border: 'none', borderRadius: 10,
            padding: '12px 32px', fontSize: '0.95rem', fontWeight: 700,
            boxShadow: detecting ? 'none' : '0 2px 8px rgba(249, 115, 22, 0.35)',
            cursor: detecting ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 8,
            transition: 'background 0.15s',
          }}
        >
          {detecting ? (
            <>
              <SpinnerIcon />
              Detecting…
            </>
          ) : (
            <>
              Start Analysis
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
            </>
          )}
        </button>
      </div>

      {/* Keyframe for spinner */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
